import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { InlineLoader } from '../../components/common/Loader';
import { getPlatformRevenue } from '../../services/adminService';
import { formatCurrency } from '../../utils/helpers';

function StatBadge({ label, value, icon, color, bg }) {
  return (
    <div className={`rounded-2xl p-4 flex items-center gap-3 ${bg} border border-border`}>
      <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center flex-shrink-0">
        <i className={`fa-solid ${icon} ${color} text-base`} />
      </div>
      <div>
        <p className="text-xs text-text-muted">{label}</p>
        <p className={`text-xl font-bold ${color} font-heading`}>{value}</p>
      </div>
    </div>
  );
}

export default function AdminRevenue() {
  const today = new Date().toISOString().split('T')[0];
  const monthStart = today.slice(0, 7) + '-01';

  const [from, setFrom]     = useState(monthStart);
  const [to, setTo]         = useState(today);
  const [groupBy, setGroupBy] = useState('day');
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    getPlatformRevenue({ from, to, groupBy })
      .then(res => setData(res))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [from, to, groupBy]);

  useEffect(() => { load(); }, [load]);

  // Bar chart: max revenue in byDate for scaling
  const maxRevenue = data?.byDate?.length
    ? Math.max(...data.byDate.map(d => d.revenue), 1)
    : 1;

  return (
    <AdminLayout title="Platform Revenue">
      <div className="space-y-4 max-w-3xl">

        {/* ── Filters ── */}
        <div className="bg-white rounded-2xl border border-border p-4 space-y-3">
          <h3 className="text-sm font-bold text-text-primary">Filter by Date Range</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="input-label">From</label>
              <input type="date" value={from} onChange={e => setFrom(e.target.value)}
                max={to} className="input" />
            </div>
            <div>
              <label className="input-label">To</label>
              <input type="date" value={to} onChange={e => setTo(e.target.value)}
                min={from} max={today} className="input" />
            </div>
          </div>
          <div className="flex gap-2">
            {['day', 'month'].map(g => (
              <button key={g} onClick={() => setGroupBy(g)}
                className={`flex-1 py-2 rounded-xl text-sm font-semibold transition-all ${
                  groupBy === g ? 'bg-primary text-white' : 'bg-surface text-text-secondary border border-border'
                }`}>
                {g === 'day' ? 'Day-wise' : 'Month-wise'}
              </button>
            ))}
          </div>
          <button onClick={load} disabled={loading}
            className="btn-primary flex items-center justify-center gap-2">
            <i className="fa-solid fa-filter text-sm" />
            {loading ? 'Loading...' : 'Apply Filter'}
          </button>
        </div>

        {loading ? <InlineLoader text="Loading revenue data..." /> : data && (
          <>
            {/* ── Summary Cards ── */}
            <div className="grid grid-cols-2 gap-3">
              <StatBadge
                label="Total Revenue"
                value={formatCurrency(data.totalRevenue)}
                icon="fa-indian-rupee-sign"
                color="text-success"
                bg="bg-green-50"
              />
              <StatBadge
                label="Transactions"
                value={data.totalTransactions}
                icon="fa-receipt"
                color="text-primary"
                bg="bg-primary-light"
              />
              <StatBadge
                label="Convenience Charges"
                value={formatCurrency(data.summary.convenienceChargeTotal)}
                icon="fa-hand-holding-dollar"
                color="text-amber-600"
                bg="bg-amber-50"
              />
              <StatBadge
                label="GST Collected"
                value={formatCurrency(data.summary.gstTotal)}
                icon="fa-percent"
                color="text-info"
                bg="bg-blue-50"
              />
            </div>

            {/* ── Bar Chart ── */}
            {data.byDate?.length > 0 ? (
              <div className="bg-white rounded-2xl border border-border p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-bold text-text-primary">Revenue Trend</h3>
                  <span className="text-xs text-text-muted">{data.filters.groupBy === 'month' ? 'Monthly' : 'Daily'}</span>
                </div>

                {/* Simple bar chart */}
                <div className="overflow-x-auto">
                  <div className="flex items-end gap-1.5 min-w-0 h-40 pb-6 relative"
                    style={{ minWidth: data.byDate.length > 15 ? `${data.byDate.length * 32}px` : '100%' }}>
                    {/* Y-axis label */}
                    <div className="absolute left-0 top-0 bottom-6 flex flex-col justify-between pointer-events-none">
                      <span className="text-[9px] text-text-muted">{formatCurrency(maxRevenue)}</span>
                      <span className="text-[9px] text-text-muted">₹0</span>
                    </div>
                    <div className="flex items-end gap-1.5 flex-1 pl-8 h-full">
                      {data.byDate.map((d, i) => {
                        const pct = maxRevenue > 0 ? (d.revenue / maxRevenue) * 100 : 0;
                        const label = data.filters.groupBy === 'month'
                          ? d.date.slice(5) // "05" for May
                          : d.date.slice(5); // "05-07"
                        return (
                          <div key={i} className="flex flex-col items-center gap-1 flex-1 min-w-[24px] h-full justify-end group relative">
                            {/* Tooltip */}
                            <div className="absolute bottom-full mb-1 hidden group-hover:flex flex-col items-center z-10">
                              <div className="bg-gray-900 text-white text-[10px] rounded-lg px-2 py-1 whitespace-nowrap">
                                <p>{d.date}</p>
                                <p className="font-bold">{formatCurrency(d.revenue)}</p>
                                <p>{d.transactions} txn</p>
                              </div>
                              <div className="w-2 h-2 bg-gray-900 rotate-45 -mt-1" />
                            </div>
                            <div
                              className="w-full rounded-t-md bg-primary transition-all hover:bg-primary/80"
                              style={{ height: `${Math.max(pct, 2)}%` }}
                            />
                            <span className="text-[8px] text-text-muted truncate w-full text-center">{label}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-border p-8 text-center">
                <i className="fa-solid fa-chart-bar text-text-muted text-3xl mb-3 block" />
                <p className="text-sm font-semibold text-text-primary">No revenue data</p>
                <p className="text-xs text-text-muted mt-1">No payments found in this date range</p>
              </div>
            )}

            {/* ── Date-wise Table ── */}
            {data.byDate?.length > 0 && (
              <div className="bg-white rounded-2xl border border-border overflow-hidden">
                <div className="px-4 py-3 border-b border-border bg-surface">
                  <h3 className="text-sm font-bold text-text-primary">
                    {groupBy === 'month' ? 'Month-wise' : 'Day-wise'} Breakdown
                  </h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-surface border-b border-border">
                        <th className="text-left px-4 py-2.5 text-xs font-semibold text-text-muted">
                          {groupBy === 'month' ? 'Month' : 'Date'}
                        </th>
                        <th className="text-right px-4 py-2.5 text-xs font-semibold text-text-muted">Revenue</th>
                        <th className="text-right px-4 py-2.5 text-xs font-semibold text-text-muted">Conv. Charge</th>
                        <th className="text-right px-4 py-2.5 text-xs font-semibold text-text-muted">GST</th>
                        <th className="text-right px-4 py-2.5 text-xs font-semibold text-text-muted">Txns</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...data.byDate].reverse().map((row, i) => (
                        <tr key={i} className="border-b border-border last:border-0 hover:bg-surface transition-colors">
                          <td className="px-4 py-3 font-medium text-text-primary">{row.date}</td>
                          <td className="px-4 py-3 text-right font-bold text-success">{formatCurrency(row.revenue)}</td>
                          <td className="px-4 py-3 text-right text-text-secondary">{formatCurrency(row.convenienceChargeTotal)}</td>
                          <td className="px-4 py-3 text-right text-text-secondary">{formatCurrency(row.gstTotal)}</td>
                          <td className="px-4 py-3 text-right">
                            <span className="bg-primary-light text-primary text-xs font-bold px-2 py-0.5 rounded-full">
                              {row.transactions}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    {/* Total row */}
                    <tfoot>
                      <tr className="bg-green-50 border-t-2 border-primary/20">
                        <td className="px-4 py-3 font-bold text-text-primary">Total</td>
                        <td className="px-4 py-3 text-right font-bold text-success">{formatCurrency(data.totalRevenue)}</td>
                        <td className="px-4 py-3 text-right font-bold text-text-secondary">{formatCurrency(data.summary.convenienceChargeTotal)}</td>
                        <td className="px-4 py-3 text-right font-bold text-text-secondary">{formatCurrency(data.summary.gstTotal)}</td>
                        <td className="px-4 py-3 text-right font-bold text-primary">{data.totalTransactions}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}
